import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import GlobalStyles from './styles/GlobalStyles';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import ToastProvider from './components/Toast';
import { useSEO } from './hooks/useSEO';
import { initCSRF } from './utils/csrf';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Contacts from './pages/Contacts';
import Catalog from './pages/Catalog';
import Referral from './pages/Referral';
import Services from './pages/Services';
import Delivery from './pages/Delivery';
import Returns from './pages/Returns';
import Profile from './pages/Profile';
import Dashboard from './pages/dashboard/Dashboard';
import Marketing from './pages/Marketing';
import Finances from './pages/Finances';
import DashboardFinances from './pages/dashboard/Finances';
import Shop from './pages/Shop';
import News from './pages/News';
import Webinar from './pages/Webinar';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CartPage from './pages/CartPage';
import FavoritesPage from './pages/FavoritesPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFail from './pages/PaymentFail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Oferta from './pages/Oferta';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import Team from './pages/dashboard/Team';
import ReferralProgram from './pages/dashboard/ReferralProgram';
import OrdersHistory from './pages/dashboard/OrdersHistory';
import RewardsReport from './pages/dashboard/RewardsReport';
import Settings from './pages/dashboard/Settings';
import ProductDetail from './pages/ProductDetail';
import NewsDetail from './pages/NewsDetail';
import Notifications from './pages/dashboard/Notifications';
import CookieBanner from './components/CookieBanner';
import ExpertCabinet from './pages/dashboard/ExpertCabinet';
import MyPrograms from './pages/dashboard/MyPrograms';
import ExpertsList from './pages/ExpertsList';
import ExpertPage from './pages/ExpertPage';
import DigitalProductPage from './pages/DigitalProductPage';
import BecomeExpert from './pages/BecomeExpert';
import CookiePolicy from './pages/CookiePolicy';
import PersonalDataConsent from './pages/PersonalDataConsent';
import Certificates from './pages/Certificates';
import MobileBottomMenu from './components/MobileBottomMenu';
import ScrollToTop from './components/ScrollToTop';

function AppContent() {
  const location = useLocation();
  
  // Загружаем и применяем SEO настройки глобально
  useSEO();
  
  // Инициализация CSRF токена при загрузке приложения
  useEffect(() => {
    initCSRF();
  }, []);
  
  // Проверяем, является ли текущая страница страницей dashboard
  const isDashboardPage = location.pathname.startsWith('/dashboard') || 
                          location.pathname.startsWith('/marketing') || 
                          location.pathname.startsWith('/finances') || 
                          location.pathname.startsWith('/shop') || 
                          location.pathname.startsWith('/webinar');
  
  // Проверяем, является ли текущая страница страницей личного кабинета
  const isProfilePage = location.pathname === '/profile' || 
                        location.pathname === '/orders';
  
  // Плашка с куки показывается только на основном сайте (не в админ-панели и не в личном кабинете)
  const shouldShowCookieBanner = !isDashboardPage && !isProfilePage;

  return (
    <div className="App">
      <ScrollToTop />
      {!isDashboardPage && <Header />}
      <main className={!isDashboardPage ? 'has-mobile-menu' : ''}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/catalog/:categorySlug" element={<Catalog />} />
            <Route path="/referral" element={<Referral />} />
            <Route path="/about" element={<Services />} />
            <Route path="/delivery" element={<Delivery />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/privacy_policy" element={<PrivacyPolicy />} />
            <Route path="/oferta" element={<Oferta />} />
            <Route path="/vozvrat" element={<Returns />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/user/registration" element={<RegisterPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            } />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            } />
            <Route path="/payment" element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            } />
            <Route path="/payment/success" element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            } />
            <Route path="/payment/fail" element={
              <ProtectedRoute>
                <PaymentFail />
              </ProtectedRoute>
            } />
            <Route path="/orders" element={<OrdersHistory />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/finances" element={<Finances />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/news" element={<News />} />
            <Route path="/webinar" element={<Webinar />} />
            <Route path="/dashboard/team" element={<Team />} />
            <Route path="/dashboard/referral-program" element={<ReferralProgram />} />
            <Route path="/dashboard/orders-history" element={<OrdersHistory />} />
            <Route path="/dashboard/rewards-report" element={<RewardsReport />} />
            <Route path="/dashboard/finances" element={<DashboardFinances />} />
            <Route path="/dashboard/notifications" element={<Notifications />} />
            <Route path="/dashboard/settings" element={<Settings />} />
            <Route path="/dashboard/expert" element={
              <ProtectedRoute>
                <ExpertCabinet />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/my-programs" element={
              <ProtectedRoute>
                <MyPrograms />
              </ProtectedRoute>
            } />
            <Route path="/experts" element={<ExpertsList />} />
            <Route path="/expert/:slug" element={<ExpertPage />} />
            <Route path="/expert/:slug/program/:programSlug" element={<DigitalProductPage />} />
            <Route path="/become-expert" element={<BecomeExpert />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/personal-data-consent" element={<PersonalDataConsent />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {!isDashboardPage && <Footer />}
        {!isDashboardPage && <MobileBottomMenu />}
        {shouldShowCookieBanner && <CookieBanner location={location} />}
      </div>
    );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <FavoritesProvider>
          <ToastProvider>
            <Router>
              <GlobalStyles />
              <AppContent />
            </Router>
          </ToastProvider>
        </FavoritesProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
